import type {
  AuditEntry,
  Conversation,
  Deliverable,
  Invoice,
  Message,
  MessageRead,
  OrgMember,
  Organisation,
  Payment,
  PortalNotification,
  PortalUser,
  Product,
  Project,
  ProjectMember,
  Task,
} from '../types/portal'

/**
 * Demonstration records for the client portal preview. Every row mirrors a table
 * in the backend specification. The organisations are illustrative and do not
 * represent real clients.
 *
 * The demo user, Ama Mensah, is deliberately an owner at one organisation and a
 * viewer at another, so the preview shows roles being scoped per organisation.
 */

export const PRODUCTS: Product[] = [
  {
    id: 'prod-solution',
    slug: 'solution',
    name: 'Cavalla Solution',
    description: 'Consulting deliverables, research reports and strategy.',
  },
  {
    id: 'prod-gov',
    slug: 'gov',
    name: 'Cavalla Gov',
    description: 'National plan delivery tracking, development finance and AI reporting.',
  },
]

export const ORGANISATIONS: Organisation[] = [
  {
    id: 'org-northbridge',
    name: 'Northbridge Foundation',
    slug: 'northbridge',
    kind: 'Development foundation',
    productIds: ['prod-solution'],
  },
  {
    id: 'org-planning',
    name: 'Ministry of Planning (sample)',
    slug: 'ministry-of-planning',
    kind: 'Government ministry',
    productIds: ['prod-gov', 'prod-solution'],
  },
]

export const USERS: PortalUser[] = [
  {
    id: 'u-amensah',
    email: 'a.mensah@northbridge.org',
    firstName: 'Ama',
    lastName: 'Mensah',
    jobTitle: 'Director of Programmes',
    isInternal: false,
    internalRole: null,
    lastLoginAt: '2026-09-25T08:14:00Z',
  },
  {
    id: 'u-fbah',
    email: 'f.bah@northbridge.org',
    firstName: 'Fatou',
    lastName: 'Bah',
    jobTitle: 'Monitoring and Evaluation Officer',
    isInternal: false,
    internalRole: null,
    lastLoginAt: '2026-09-24T13:02:00Z',
  },
  {
    id: 'u-jdiallo',
    email: 'j.diallo@northbridge.org',
    firstName: 'Jean',
    lastName: 'Diallo',
    jobTitle: 'Board Liaison',
    isInternal: false,
    internalRole: null,
    lastLoginAt: '2026-09-10T09:40:00Z',
  },
  {
    // Invited, never signed in, so the first magic link leads to onboarding.
    id: 'u-kosei',
    email: 'k.osei@northbridge.org',
    firstName: null,
    lastName: null,
    jobTitle: null,
    isInternal: false,
    internalRole: null,
    lastLoginAt: null,
  },
  {
    id: 'u-tkamara',
    email: 't.kamara@planning.gov.example',
    firstName: 'Tamba',
    lastName: 'Kamara',
    jobTitle: 'Director, Monitoring and Evaluation',
    isInternal: false,
    internalRole: null,
    lastLoginAt: '2026-09-26T10:21:00Z',
  },
  {
    id: 'u-jclarke',
    email: 'jonathan@cavalladata.com',
    firstName: 'Jonathan',
    lastName: 'Clarke',
    jobTitle: 'Founder, Cavalla',
    isInternal: true,
    internalRole: 'superadmin',
    lastLoginAt: '2026-09-28T07:30:00Z',
  },
  {
    id: 'u-nuwase',
    email: 'n.uwase@cavalladata.com',
    firstName: 'Nadia',
    lastName: 'Uwase',
    jobTitle: 'Engagement Lead, Cavalla',
    isInternal: true,
    internalRole: 'staff',
    lastLoginAt: '2026-09-28T06:55:00Z',
  },
]

export const ORG_MEMBERS: OrgMember[] = [
  { id: 'om-1', orgId: 'org-northbridge', userId: 'u-amensah', role: 'owner', invitedBy: 'u-jclarke', joinedAt: '2026-03-02T09:00:00Z' },
  { id: 'om-2', orgId: 'org-northbridge', userId: 'u-fbah', role: 'member', invitedBy: 'u-amensah', joinedAt: '2026-03-09T09:00:00Z' },
  { id: 'om-3', orgId: 'org-northbridge', userId: 'u-jdiallo', role: 'viewer', invitedBy: 'u-amensah', joinedAt: '2026-05-14T09:00:00Z' },
  { id: 'om-4', orgId: 'org-northbridge', userId: 'u-kosei', role: 'member', invitedBy: 'u-amensah', joinedAt: '2026-09-22T09:00:00Z' },
  { id: 'om-5', orgId: 'org-planning', userId: 'u-tkamara', role: 'owner', invitedBy: 'u-jclarke', joinedAt: '2026-06-01T09:00:00Z' },
  { id: 'om-6', orgId: 'org-planning', userId: 'u-amensah', role: 'viewer', invitedBy: 'u-tkamara', joinedAt: '2026-07-18T09:00:00Z' },
]

export const PROJECTS: Project[] = [
  {
    id: 'p-agri-scan',
    orgId: 'org-northbridge',
    productId: 'prod-solution',
    name: 'West Africa agri-finance market scan',
    description:
      'Market sizing, competitor map and demand signals for smallholder finance across Ghana, Côte d’Ivoire and Senegal, to decide where the foundation’s 2027 grant round should go first.',
    status: 'active',
    startDate: '2026-07-06',
    endDate: '2026-11-13',
    createdBy: 'u-nuwase',
    updatedAt: '2026-09-26T15:10:00Z',
  },
  {
    id: 'p-donor-strategy',
    orgId: 'org-northbridge',
    productId: 'prod-solution',
    name: 'Donor engagement strategy 2027',
    description:
      'A go to market plan for co-funding partners: segment the donor landscape, set the pitch for each segment and build the first quarter pipeline.',
    status: 'active',
    startDate: '2026-08-17',
    endDate: '2026-12-11',
    createdBy: 'u-nuwase',
    updatedAt: '2026-09-24T11:00:00Z',
  },
  {
    id: 'p-ai-readiness',
    orgId: 'org-northbridge',
    productId: 'prod-solution',
    name: 'AI readiness review',
    description: 'A review of data, skills and governance ahead of any AI investment, with a costed roadmap.',
    status: 'completed',
    startDate: '2026-03-09',
    endDate: '2026-05-29',
    createdBy: 'u-jclarke',
    updatedAt: '2026-05-29T16:00:00Z',
  },
  {
    id: 'p-delivery-pilot',
    orgId: 'org-planning',
    productId: 'prod-gov',
    name: 'National plan delivery tracker pilot',
    description:
      'Delivery Tracker live for the ministry and four pilot agencies, with the first quarterly monitoring report drafted from live figures and approved by a named official.',
    status: 'active',
    startDate: '2026-06-15',
    endDate: '2026-12-18',
    createdBy: 'u-jclarke',
    updatedAt: '2026-09-27T09:45:00Z',
  },
  {
    id: 'p-dfi-mapping',
    orgId: 'org-planning',
    productId: 'prod-solution',
    name: 'Development finance mapping',
    description: 'Map partner commitments and disbursements against plan programmes for the annual partner review.',
    status: 'paused',
    startDate: '2026-08-03',
    endDate: '2026-10-30',
    createdBy: 'u-jclarke',
    updatedAt: '2026-09-12T10:00:00Z',
  },
]

export const PROJECT_MEMBERS: ProjectMember[] = [
  { projectId: 'p-agri-scan', userId: 'u-amensah', role: 'lead' },
  { projectId: 'p-agri-scan', userId: 'u-fbah', role: 'contributor' },
  { projectId: 'p-agri-scan', userId: 'u-nuwase', role: 'lead' },
  { projectId: 'p-donor-strategy', userId: 'u-amensah', role: 'lead' },
  { projectId: 'p-donor-strategy', userId: 'u-jdiallo', role: 'observer' },
  { projectId: 'p-donor-strategy', userId: 'u-nuwase', role: 'lead' },
  { projectId: 'p-ai-readiness', userId: 'u-amensah', role: 'lead' },
  { projectId: 'p-ai-readiness', userId: 'u-jclarke', role: 'lead' },
  { projectId: 'p-delivery-pilot', userId: 'u-tkamara', role: 'lead' },
  { projectId: 'p-delivery-pilot', userId: 'u-amensah', role: 'observer' },
  { projectId: 'p-delivery-pilot', userId: 'u-jclarke', role: 'lead' },
  { projectId: 'p-dfi-mapping', userId: 'u-tkamara', role: 'lead' },
  { projectId: 'p-dfi-mapping', userId: 'u-jclarke', role: 'lead' },
]

export const TASKS: Task[] = [
  { id: 't-1', projectId: 'p-agri-scan', title: 'Confirm the three priority value chains', description: 'Cocoa, cashew and rice are proposed. Confirm or swap before sizing starts.', status: 'done', assignedTo: 'u-amensah', dueDate: '2026-07-24', sortOrder: 1, createdBy: 'u-nuwase' },
  { id: 't-2', projectId: 'p-agri-scan', title: 'Share partner lender contact list', description: null, status: 'done', assignedTo: 'u-fbah', dueDate: '2026-08-07', sortOrder: 2, createdBy: 'u-nuwase' },
  { id: 't-3', projectId: 'p-agri-scan', title: 'Review draft market sizing for Ghana', description: 'Section 2 of the interim report. Comments in the document are fine.', status: 'review', assignedTo: 'u-amensah', dueDate: '2026-10-02', sortOrder: 3, createdBy: 'u-nuwase' },
  { id: 't-4', projectId: 'p-agri-scan', title: 'Field interviews, Côte d’Ivoire', description: null, status: 'in_progress', assignedTo: 'u-nuwase', dueDate: '2026-10-16', sortOrder: 4, createdBy: 'u-nuwase' },
  { id: 't-5', projectId: 'p-agri-scan', title: 'Provide 2025 grant outcome data', description: 'Disbursed amount and repayment rate per grantee, if available.', status: 'todo', assignedTo: 'u-fbah', dueDate: '2026-10-09', sortOrder: 5, createdBy: 'u-nuwase' },
  { id: 't-6', projectId: 'p-donor-strategy', title: 'Approve donor segment definitions', description: null, status: 'in_progress', assignedTo: 'u-amensah', dueDate: '2026-10-05', sortOrder: 1, createdBy: 'u-nuwase' },
  { id: 't-7', projectId: 'p-donor-strategy', title: 'Draft pitch narrative for bilateral donors', description: null, status: 'todo', assignedTo: 'u-nuwase', dueDate: '2026-10-23', sortOrder: 2, createdBy: 'u-nuwase' },
  { id: 't-8', projectId: 'p-delivery-pilot', title: 'Agency data leads nominated', description: null, status: 'done', assignedTo: 'u-tkamara', dueDate: '2026-07-10', sortOrder: 1, createdBy: 'u-jclarke' },
  { id: 't-9', projectId: 'p-delivery-pilot', title: 'Approve Q3 monitoring report draft', description: 'AI assisted draft, labelled as such. Needs a named official’s approval before release.', status: 'review', assignedTo: 'u-tkamara', dueDate: '2026-10-07', sortOrder: 2, createdBy: 'u-jclarke' },
]

export const DELIVERABLES: Deliverable[] = [
  { id: 'd-1', projectId: 'p-agri-scan', name: 'Inception report', description: 'Scope, method and workplan.', filename: 'cavalla-agri-scan-inception.pdf', mimeType: 'application/pdf', sizeBytes: 1_420_000, version: 1, parentId: null, uploadedBy: 'u-nuwase', createdAt: '2026-07-17T10:00:00Z', deletedAt: null },
  { id: 'd-2', projectId: 'p-agri-scan', name: 'Interim market sizing', description: 'Ghana section complete, two markets in progress.', filename: 'cavalla-agri-scan-interim-v1.pdf', mimeType: 'application/pdf', sizeBytes: 2_860_000, version: 1, parentId: null, uploadedBy: 'u-nuwase', createdAt: '2026-09-11T14:20:00Z', deletedAt: null },
  { id: 'd-3', projectId: 'p-agri-scan', name: 'Interim market sizing', description: 'Revised after the 18 September call.', filename: 'cavalla-agri-scan-interim-v2.pdf', mimeType: 'application/pdf', sizeBytes: 3_010_000, version: 2, parentId: 'd-2', uploadedBy: 'u-nuwase', createdAt: '2026-09-26T15:10:00Z', deletedAt: null },
  { id: 'd-4', projectId: 'p-agri-scan', name: 'Grantee dataset, 2025', description: null, filename: 'northbridge-grantees-2025.xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', sizeBytes: 486_000, version: 1, parentId: null, uploadedBy: 'u-fbah', createdAt: '2026-08-06T09:30:00Z', deletedAt: null },
  { id: 'd-5', projectId: 'p-donor-strategy', name: 'Donor landscape map', description: null, filename: 'cavalla-donor-landscape.pdf', mimeType: 'application/pdf', sizeBytes: 1_980_000, version: 1, parentId: null, uploadedBy: 'u-nuwase', createdAt: '2026-09-18T11:00:00Z', deletedAt: null },
  { id: 'd-6', projectId: 'p-ai-readiness', name: 'AI readiness report and roadmap', description: 'Final.', filename: 'cavalla-ai-readiness-final.pdf', mimeType: 'application/pdf', sizeBytes: 4_120_000, version: 1, parentId: null, uploadedBy: 'u-jclarke', createdAt: '2026-05-29T16:00:00Z', deletedAt: null },
  { id: 'd-7', projectId: 'p-delivery-pilot', name: 'Q3 monitoring report, AI assisted draft', description: 'Drafted from live figures. Pending approval by a named official.', filename: 'q3-monitoring-report-draft.docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', sizeBytes: 912_000, version: 1, parentId: null, uploadedBy: 'u-jclarke', createdAt: '2026-09-27T09:45:00Z', deletedAt: null },
]

export const CONVERSATIONS: Conversation[] = [
  { id: 'c-1', orgId: 'org-northbridge', projectId: 'p-agri-scan', subject: 'West Africa agri-finance market scan', updatedAt: '2026-09-26T15:14:00Z' },
  { id: 'c-2', orgId: 'org-northbridge', projectId: 'p-donor-strategy', subject: 'Donor engagement strategy 2027', updatedAt: '2026-09-23T10:02:00Z' },
  { id: 'c-3', orgId: 'org-northbridge', projectId: null, subject: 'Invoice address change', updatedAt: '2026-09-15T12:30:00Z' },
  { id: 'c-4', orgId: 'org-planning', projectId: 'p-delivery-pilot', subject: 'National plan delivery tracker pilot', updatedAt: '2026-09-27T09:50:00Z' },
]

export const MESSAGES: Message[] = [
  { id: 'm-1', conversationId: 'c-1', senderId: 'u-amensah', body: 'Thanks for the call on Thursday. Can the revised interim reflect the lower cashew volumes we discussed?', createdAt: '2026-09-19T08:40:00Z', attachments: [] },
  { id: 'm-2', conversationId: 'c-1', senderId: 'u-nuwase', body: 'Yes. We have revised the cashew estimate using the 2025 export figures and stated the confidence range. Version 2 is now in Deliverables.', createdAt: '2026-09-26T15:14:00Z', attachments: [] },
  { id: 'm-3', conversationId: 'c-2', senderId: 'u-nuwase', body: 'The landscape map is uploaded. We would value your view on whether foundations and DFIs should sit in one segment or two.', createdAt: '2026-09-23T10:02:00Z', attachments: [] },
  { id: 'm-4', conversationId: 'c-3', senderId: 'u-amensah', body: 'Please address future invoices to our Accra office.', createdAt: '2026-09-15T11:05:00Z', attachments: [] },
  { id: 'm-5', conversationId: 'c-3', senderId: 'u-jclarke', body: 'Done. INV-2026-014 onwards carries the Accra address.', createdAt: '2026-09-15T12:30:00Z', attachments: [] },
  { id: 'm-6', conversationId: 'c-4', senderId: 'u-jclarke', body: 'The Q3 draft is ready for approval. Every figure links to the agency return it came from.', createdAt: '2026-09-27T09:50:00Z', attachments: [] },
]

export const MESSAGE_READS: MessageRead[] = [
  { userId: 'u-amensah', conversationId: 'c-1', lastReadAt: '2026-09-19T08:40:00Z' },
  { userId: 'u-amensah', conversationId: 'c-3', lastReadAt: '2026-09-15T12:30:00Z' },
]

export const INVOICES: Invoice[] = [
  {
    id: 'inv-1', orgId: 'org-northbridge', projectId: 'p-ai-readiness', invoiceNumber: 'INV-2026-006', status: 'paid', currency: 'USD',
    subtotalCents: 350_000, taxCents: 0, totalCents: 350_000, dueDate: '2026-06-28', paidAt: '2026-06-20T10:00:00Z', sentAt: '2026-05-29T16:30:00Z', notes: null, billedTo: 'u-amensah',
    lines: [{ id: 'l-1', description: 'AI readiness review, fixed fee', quantity: 1, unitPriceCents: 350_000, amountCents: 350_000 }],
  },
  {
    id: 'inv-2', orgId: 'org-northbridge', projectId: 'p-agri-scan', invoiceNumber: 'INV-2026-014', status: 'overdue', currency: 'USD',
    subtotalCents: 480_000, taxCents: 0, totalCents: 480_000, dueDate: '2026-09-15', paidAt: null, sentAt: '2026-08-16T09:00:00Z', notes: 'Milestone 1 of 2: inception and interim report.', billedTo: 'u-amensah',
    lines: [
      { id: 'l-2', description: 'Market scan, milestone 1: inception', quantity: 1, unitPriceCents: 180_000, amountCents: 180_000 },
      { id: 'l-3', description: 'Market scan, milestone 1: interim report', quantity: 1, unitPriceCents: 300_000, amountCents: 300_000 },
    ],
  },
  {
    id: 'inv-3', orgId: 'org-northbridge', projectId: 'p-agri-scan', invoiceNumber: 'INV-2026-019', status: 'sent', currency: 'USD',
    subtotalCents: 64_000, taxCents: 0, totalCents: 64_000, dueDate: '2026-10-24', paidAt: null, sentAt: '2026-09-24T09:00:00Z', notes: 'Field travel for grantee interviews, at cost.', billedTo: 'u-fbah',
    lines: [
      { id: 'l-4', description: 'Field travel, Accra and Kumasi', quantity: 1, unitPriceCents: 38_000, amountCents: 38_000 },
      { id: 'l-5', description: 'Interview facilitation, per day', quantity: 2, unitPriceCents: 13_000, amountCents: 26_000 },
    ],
  },
  {
    id: 'inv-4', orgId: 'org-planning', projectId: 'p-delivery-pilot', invoiceNumber: 'INV-2026-011', status: 'sent', currency: 'USD',
    subtotalCents: 1_200_000, taxCents: 0, totalCents: 1_200_000, dueDate: '2026-10-31', paidAt: null, sentAt: '2026-09-01T09:00:00Z', notes: 'Financed as development partner technical assistance.', billedTo: 'u-tkamara',
    lines: [{ id: 'l-6', description: 'Delivery tracker pilot, phase 1', quantity: 1, unitPriceCents: 1_200_000, amountCents: 1_200_000 }],
  },
]

export const PAYMENTS: Payment[] = [
  { id: 'pay-1', invoiceId: 'inv-1', amountCents: 350_000, currency: 'USD', method: 'Bank transfer', status: 'succeeded', paidAt: '2026-06-20T10:00:00Z' },
]

export const AUDIT_LOG: AuditEntry[] = [
  { id: 'a-1', orgId: 'org-northbridge', userId: 'u-nuwase', action: 'deliverable.uploaded', entityType: 'deliverable', entityId: 'd-3', metadata: { name: 'Interim market sizing', version: '2' }, createdAt: '2026-09-26T15:10:00Z' },
  { id: 'a-2', orgId: 'org-northbridge', userId: 'u-amensah', action: 'member.invited', entityType: 'user', entityId: 'u-kosei', metadata: { email: 'k.osei@northbridge.org', role: 'member' }, createdAt: '2026-09-22T09:00:00Z' },
  { id: 'a-3', orgId: 'org-northbridge', userId: 'u-fbah', action: 'deliverable.downloaded', entityType: 'deliverable', entityId: 'd-2', metadata: { name: 'Interim market sizing', version: '1' }, createdAt: '2026-09-12T08:15:00Z' },
  { id: 'a-4', orgId: 'org-northbridge', userId: 'u-jclarke', action: 'invoice.sent', entityType: 'invoice', entityId: 'inv-3', metadata: { number: 'INV-2026-019' }, createdAt: '2026-09-24T09:00:00Z' },
]

export const NOTIFICATIONS: PortalNotification[] = [
  { id: 'n-1', userId: 'u-amensah', type: 'deliverable.uploaded', title: 'Interim market sizing, version 2', body: 'Uploaded to West Africa agri-finance market scan.', link: '/portal/orgs/org-northbridge/projects/p-agri-scan?tab=deliverables', isRead: false, createdAt: '2026-09-26T15:10:00Z' },
  { id: 'n-2', userId: 'u-amensah', type: 'message.received', title: 'New message from Nadia Uwase', body: 'Donor engagement strategy 2027', link: '/portal/orgs/org-northbridge/messages?c=c-2', isRead: false, createdAt: '2026-09-23T10:02:00Z' },
  { id: 'n-3', userId: 'u-amensah', type: 'invoice.overdue', title: 'INV-2026-014 is overdue', body: 'Due 15 September 2026.', link: '/portal/orgs/org-northbridge/invoices', isRead: false, createdAt: '2026-09-16T07:00:00Z' },
  { id: 'n-4', userId: 'u-amensah', type: 'task.assigned', title: 'Review draft market sizing for Ghana', body: 'Due 2 October 2026.', link: '/portal/orgs/org-northbridge/projects/p-agri-scan?tab=tasks', isRead: true, createdAt: '2026-09-11T14:25:00Z' },
]
